import { useState } from 'react';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Stack,
  Group,
  Button,
  Badge,
  List,
  Alert,
  Loader,
  Modal,
  Divider
} from '@mantine/core';
import {
  IconCheck,
  IconCrown,
  IconStar,
  IconExclamationCircle,
  IconCreditCard
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useApi, useLiveQuery, useApiClient, useSession } from '../api';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatUsd = (cents) => (cents == null ? '' : usd.format(cents / 100));

const SubscriptionPage = () => {
  const api = useApiClient();
  const { me } = useSession();
  const [subscribing, setSubscribing] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Public plan catalog: {items: [Plan], payments_enabled}
  const { data: plansData, loading: plansLoading } = useApi('/api/v1/subscription/plans');
  const plans = plansData?.items || [];
  const paymentsEnabled = !!plansData?.payments_enabled;

  // Own subscription, kept live via subscription.changed on the account topic
  const {
    data: subData,
    loading: subLoading,
    refetch
  } = useLiveQuery('/api/v1/me/subscription', undefined, {
    topics: [me && `user.${me.clerk_user_id}.account`],
  });

  const subscription = subData?.subscription || null;
  const isActive = subscription?.status === 'active';
  const isPastDue = subscription?.status === 'past_due';
  // §4.4 grace: access continues until grace_until while the card is retried
  const inGrace =
    isPastDue && subscription?.grace_until && new Date(subscription.grace_until) > new Date();
  const planName = (planId) => plans.find((p) => p.id === planId)?.name || planId;

  const handleSubscribe = async (planId) => {
    try {
      setSubscribing(true);
      if (paymentsEnabled) {
        // CCBill checkout (contract §4.4): redirect to the hosted payment
        // page; the webhook activates the subscription and the account
        // topic's subscription.changed refreshes this page on return.
        const { url } = await api.post('/api/v1/me/subscription/checkout', { plan: planId });
        window.location.assign(url);
        return;
      }
      const created = await api.post('/api/v1/me/subscription', { plan: planId });
      notifications.show({
        title: 'Success!',
        message: `You've successfully subscribed to ${planName(created.plan)}!`,
        color: 'green'
      });
      refetch();
    } catch (err) {
      console.error('Error subscribing:', err);
      notifications.show({
        title: 'Error',
        message: err?.message || 'Failed to create subscription',
        color: 'red'
      });
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      // DELETE returns 200 with the canceled Subscription
      await api.del('/api/v1/me/subscription');
      setCancelModalOpen(false);
      notifications.show({
        title: 'Subscription Canceled',
        message: 'Your subscription has been canceled',
        color: 'yellow'
      });
      refetch();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      notifications.show({
        title: 'Error',
        message: err?.message || 'Failed to cancel subscription',
        color: 'red'
      });
    }
  };

  if (plansLoading || subLoading) {
    return (
      <ProtectedRoute requireAuth={true} redirectTo="/">
        <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
      </ProtectedRoute>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <ProtectedRoute requireAuth={true} redirectTo="/">
      <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Subscription Plans</Title>
          <Text c="dimmed" mt="xs">
            Choose the plan that's right for you
          </Text>
        </div>

        {/* Current Subscription Status */}
        {isActive && (
          <Alert
            icon={<IconCrown size="1rem" />}
            color="green"
            variant="light"
            title="Active Subscription"
          >
            <Text size="sm">
              You are currently subscribed to <strong>{planName(subscription.plan)}</strong>
              {' '}({formatUsd(subscription.price_cents)}/month)
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              Current period ends: {formatDate(subscription.current_period_end)}
            </Text>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              mt="sm"
              onClick={() => setCancelModalOpen(true)}
            >
              Cancel Subscription
            </Button>
          </Alert>
        )}

        {isPastDue && (
          <Alert
            icon={<IconExclamationCircle size="1rem" />}
            color="yellow"
            variant="light"
            title="Payment Problem"
          >
            <Text size="sm">
              We couldn't renew your <strong>{planName(subscription.plan)}</strong> subscription.
              {inGrace
                ? ` You keep full access until ${formatDate(subscription.grace_until)} while the payment is retried.`
                : ' Your access has lapsed.'}
              {' '}Re-subscribe below to set up a new payment right away.
            </Text>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              mt="sm"
              onClick={() => setCancelModalOpen(true)}
            >
              Cancel Subscription
            </Button>
          </Alert>
        )}

        {/* Subscription Plans */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {plans.map((plan) => {
            const isCurrentPlan = subscription?.plan === plan.id;
            const isCurrentActive = isActive && isCurrentPlan;

            return (
              <Card
                key={plan.id}
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                style={{
                  borderColor: isCurrentActive ? 'var(--mantine-color-green-6)' : undefined,
                  borderWidth: isCurrentActive ? 2 : 1
                }}
              >
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Group gap="xs">
                        <Title order={3}>{plan.name}</Title>
                        {plan.id === 'paypiggiepro' && (
                          <Badge color="gradient" variant="filled" size="sm">
                            PRO
                          </Badge>
                        )}
                      </Group>
                      <Title order={2} mt="xs">
                        {formatUsd(plan.price_cents)}
                        <Text span size="sm" c="dimmed" fw={400}>
                          /month
                        </Text>
                      </Title>
                    </div>
                    {plan.id === 'paypiggiepro' && (
                      <IconStar size="1.5rem" color="var(--mantine-color-yellow-6)" />
                    )}
                  </Group>

                  <Divider />

                  <List
                    spacing="sm"
                    size="sm"
                    icon={
                      <IconCheck
                        size="1rem"
                        color="var(--mantine-color-green-6)"
                      />
                    }
                  >
                    {(plan.features || []).map((feature, index) => (
                      <List.Item key={index}>{feature}</List.Item>
                    ))}
                  </List>

                  {isCurrentActive ? (
                    <Badge color="green" variant="filled" size="lg" fullWidth>
                      Current Plan
                    </Badge>
                  ) : (
                    <Button
                      fullWidth
                      variant={plan.id === 'paypiggiepro' ? 'filled' : 'light'}
                      leftSection={<IconCreditCard size="1rem" />}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={subscribing || isActive}
                      loading={subscribing}
                    >
                      {isActive
                        ? 'Already Subscribed'
                        : `Subscribe to ${plan.name}`}
                    </Button>
                  )}
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>

        {/* Benefits Section */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Title order={3}>Subscription Benefits</Title>
            <Text size="sm" c="dimmed">
              All subscriptions include:
            </Text>
            <List size="sm" spacing="xs">
              <List.Item>Instant activation</List.Item>
              <List.Item>Cancel anytime</List.Item>
              <List.Item>Secure payment processing</List.Item>
              <List.Item>24/7 customer support</List.Item>
            </List>
          </Stack>
        </Card>
      </Stack>

      {/* Cancel Subscription Modal */}
      <Modal
        opened={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Cancel Subscription"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconExclamationCircle size="1rem" />} color="yellow">
            <Text size="sm">
              Are you sure you want to cancel your subscription? You will lose access to all premium features at the end of your current billing period.
            </Text>
          </Alert>
          <Text size="sm" c="dimmed">
            Your subscription will remain active until: {subscription && formatDate(subscription.current_period_end)}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setCancelModalOpen(false)}
            >
              Keep Subscription
            </Button>
            <Button
              color="red"
              onClick={handleCancelSubscription}
            >
              Cancel Subscription
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
    </ProtectedRoute>
  );
};

export default SubscriptionPage;
